import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import {
  Globe, Zap, Shield, Sparkles, Star, Check, ArrowRight, ArrowLeft,
  Loader2, Lock, Layout, CreditCard, AlertCircle, CheckCircle2, Info,
  Link2, ChevronDown, ChevronUp, Copy, Crown, Search,
} from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: "domain", label: "Domain", icon: Globe },
  { id: "package", label: "Paket", icon: Zap },
  { id: "summary", label: "Bezahlung", icon: CreditCard },
];

const TLDS = [".de", ".com", ".net", ".at", ".ch"];

const HIGHLIGHT_FEATURES = ["Content-Update", "Backup", "A/B-Test", "Marketing", "Priority Support", "1-on-1"];

export default function CheckoutPage() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Domain state
  const [domainType, setDomainType] = useState("custom");
  const [customDomain, setCustomDomain] = useState("");
  const [selectedTld, setSelectedTld] = useState(".de");
  const [domainStatus, setDomainStatus] = useState(null);
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [domainError, setDomainError] = useState("");
  const [reservation, setReservation] = useState(null);
  const [reserving, setReserving] = useState(false);
  const [showDomainTooltip, setShowDomainTooltip] = useState(false);
  const debounceRef = useRef(null);

  // Connect-domain state
  const [connectDomain, setConnectDomain] = useState("");
  const [connectStep, setConnectStep] = useState("input"); // "input" | "instructions"
  const [detectedProvider, setDetectedProvider] = useState(null);
  const [nameservers, setNameservers] = useState([]);
  const [detectingCF, setDetectingCF] = useState(false);
  const [cfError, setCfError] = useState(null);
  const [openGuide, setOpenGuide] = useState(null);

  // Package state
  const [selectedPackage, setSelectedPackage] = useState("pro");
  const [packages, setPackages] = useState([]);

  // Summary/checkout state
  const [summary, setSummary] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [acceptedAGB, setAcceptedAGB] = useState(false);
  const [acceptedDomainRisk, setAcceptedDomainRisk] = useState(false);

  useEffect(() => {
    fetchPage();
  }, [pageId]);

  const fetchPage = async () => {
    try {
      const [pageRes] = await Promise.all([
        axios.get(`${API}/pages/${pageId}`),
      ]);
      setPage(pageRes.data);
      const industry = pageRes.data.industry || "";
      const pRes = await axios.get(`${API}/pricing?industry=${encodeURIComponent(industry)}`);
      setPackages(pRes.data.packages || []);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleDomainInput = (value) => {
    setCustomDomain(value);
    setDomainStatus(null);
    setDomainError("");
    setReservation(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length >= 3) {
      debounceRef.current = setTimeout(() => checkDomain(value + selectedTld), 600);
    }
  };

  const handleTldChange = (tld) => {
    setSelectedTld(tld);
    setDomainStatus(null);
    setReservation(null);
    if (customDomain.length >= 3) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => checkDomain(customDomain + tld), 300);
    }
  };

  const checkDomain = async (domain) => {
    setCheckingDomain(true);
    setDomainStatus(null);
    setDomainError("");
    try {
      const res = await axios.post(`${API}/domain-check-with-context/${pageId}`, { domain });
      if (res.data.error) {
        setDomainStatus("error");
        setDomainError(res.data.error);
      } else if (res.data.available) {
        setDomainStatus("available");
      } else {
        setDomainStatus("taken");
      }
    } catch {
      setDomainStatus("error");
      setDomainError("Prüfung fehlgeschlagen");
    } finally {
      setCheckingDomain(false);
    }
  };

  const reserveDomain = async () => {
    const domain = customDomain + selectedTld;
    setReserving(true);
    try {
      const res = await axios.post(`${API}/domain/reserve`, { domain, page_id: pageId });
      setReservation(res.data);
      toast.success(`${domain} für 30 Minuten reserviert!`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Reservierung fehlgeschlagen";
      toast.error(msg);
    } finally {
      setReserving(false);
    }
  };

  const goNext = async () => {
    if (step === 0 && domainType === "custom" && !reservation) {
      toast.error("Bitte Domain zuerst reservieren");
      return;
    }
    if (step === 0 && domainType === "connect" && !connectDomain.trim()) {
      toast.error("Bitte gib deine bestehende Domain ein");
      return;
    }
    if (step === 1) {
      await fetchSummary();
    }
    setStep((s) => Math.min(s + 1, 2));
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const fetchSummary = async () => {
    try {
      const domain_name = domainType === "custom" ? customDomain + selectedTld
        : domainType === "connect" ? connectDomain.trim() : undefined;
      const params = new URLSearchParams({
        package_id: selectedPackage,
        domain_type: domainType,
      });
      if (domain_name) params.set("domain_name", domain_name);
      const res = await axios.get(`${API}/checkout/summary/${pageId}?${params}`);
      setSummary(res.data);
    } catch {
      toast.error("Zusammenfassung konnte nicht geladen werden");
    }
  };

  const startCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/create`, {
        package_id: selectedPackage,
        page_id: pageId,
        origin_url: window.location.origin,
        domain_type: domainType,
        domain_name: domainType === "custom" ? customDomain + selectedTld
          : domainType === "connect" ? connectDomain.trim() : null,
        reservation_id: reservation?.reservation_id || null,
        referral_code: localStorage.getItem("wg_ref") || null,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout fehlgeschlagen");
      setCheckoutLoading(false);
    }
  };

  const setupCloudflare = async () => {
    if (!connectDomain.trim()) return;
    setDetectingCF(true);
    try {
      const provRes = await axios.post(`${API}/domain/detect-provider`, { domain: connectDomain.trim() });
      setDetectedProvider(provRes.data.provider || null);
    } catch {}
    try {
      const cfRes = await axios.post(`${API}/domain/cloudflare-setup`, { domain: connectDomain.trim() });
      setNameservers(cfRes.data.nameservers || []);
      setCfError(null);
    } catch (e) {
      setNameservers([]);
      setCfError(e.response?.data?.detail || "Cloudflare nicht verfügbar");
    }
    setDetectingCF(false);
    setConnectStep("instructions");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Kopiert!");
  };

  const CONNECT_GUIDES = {
    IONOS: ["mein.ionos.de anmelden", "Domains & SSL > Domain > Nameserver", "Andere Nameserver > NS1 + NS2 eintragen", "Speichern"],
    Strato: ["strato.de anmelden", "Domainverwaltung > Nameserver", "Fremde Nameserver > NS1 + NS2", "Speichern"],
    GoDaddy: ["godaddy.com > Domains", "DNS verwalten > Nameserver > Ändern", "Eigene Nameserver > NS1 + NS2", "Speichern"],
    "All-Inkl": ["all-inkl.com KAS anmelden", "Domains > Nameserver", "NS1 + NS2 eintragen", "Übernehmen"],
  };

  const tierConfig = {
    basis: { accent: "indigo", icon: Zap, headline: "Online sichtbar werden" },
    pro: { accent: "indigo", icon: Shield, headline: "Mehr Kunden gewinnen" },
    premium: { accent: "amber", icon: Sparkles, headline: "Marketing-Sorglos-Paket" },
  };

  const isFeatureHighlighted = (feature) => {
    return HIGHLIGHT_FEATURES.some(kw => feature.includes(kw));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="px-3 sm:px-4 md:px-8 py-4 sm:py-5 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate(`/vorschau/${pageId}`)} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-900 transition-colors text-sm" data-testid="checkout-back-btn">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Zurück zur Vorschau</span><span className="sm:hidden">Zurück</span>
          </button>
          <Logo size="md" clickable={false} />
          <div className="w-16 sm:w-24" />
        </div>
      </header>

      {/* Step Indicator - Improved with green/purple/gray */}
      <div className="px-4 py-6 bg-white border-b border-slate-100">
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-2" data-testid="checkout-steps">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <div key={s.id} className="flex items-center gap-2">
                {i > 0 && <div className={`w-8 h-0.5 ${isDone ? "bg-green-500" : isActive ? "bg-indigo-400" : "bg-slate-200"}`} />}
                <button
                  onClick={() => i < step && setStep(i)}
                  disabled={i > step}
                  data-testid={`step-${s.id}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    isDone ? "bg-green-100 text-green-700 cursor-pointer hover:bg-green-200" :
                    isActive ? "bg-indigo-600 text-white shadow-md" :
                    "bg-slate-100 text-slate-400"
                  }`}
                >
                  {isDone ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  <span className="hidden sm:inline">{s.label}</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <main className="px-4 py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            {/* STEP 1: Domain */}
            {step === 0 && (
              <motion.div key="domain" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-testid="domain-step-title">Unter welcher Adresse soll deine Seite erreichbar sein?</h2>
                  <p className="text-slate-500">Wähle, wie deine Kunden dich online finden.</p>
                </div>

                <div className="max-w-lg mx-auto space-y-4">
                  {/* Custom Domain Option – EMPFOHLEN (zuerst) */}
                  <button
                    data-testid="checkout-custom-option"
                    onClick={() => setDomainType("custom")}
                    className={`w-full p-5 rounded-xl border-2 text-left transition-all relative overflow-hidden ${
                      domainType === "custom" ? "border-indigo-500 bg-indigo-50/50" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="absolute top-0 right-0">
                      <span className="bg-indigo-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">EMPFOHLEN</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${domainType === "custom" ? "bg-indigo-100 text-indigo-600" : "bg-slate-100 text-slate-400"}`}>
                        <Globe className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 mb-1">Eigene Wunschdomain</p>
                        <p className="text-sm text-slate-500 mb-2">Professioneller Auftritt mit eigener Adresse</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><Check className="w-3 h-3 text-green-500" />Mehr Vertrauen bei Kunden</span>
                          <span className="flex items-center gap-1"><Check className="w-3 h-3 text-green-500" />Besseres Google-Ranking</span>
                        </div>
                        <p className="text-xs mt-2">
                          <span className="line-through text-slate-400">9,99€/Jahr</span>
                          <span className="ml-1.5 font-semibold text-green-600">Im Paket inklusive</span>
                        </p>
                      </div>
                      {domainType === "custom" && (
                        <div className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0 mt-1">
                          <Check className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                    </div>
                  </button>

                  {/* Custom Domain Input */}
                  {domainType === "custom" && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-3 pt-2">
                      <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100" data-testid="domain-reservation-info">
                        <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <p className="text-xs text-blue-600">Deine Domain wird für 30 Min. reserviert, bis die Zahlung abgeschlossen ist.</p>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <input data-testid="checkout-domain-input" type="text" value={customDomain}
                            onChange={(e) => handleDomainInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                            placeholder="meinfenster"
                            className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none text-base"
                          />
                          {checkingDomain && <div className="absolute right-3 top-1/2 -translate-y-1/2"><Loader2 className="w-5 h-5 text-slate-400 animate-spin" /></div>}
                        </div>
                        <select data-testid="checkout-tld-select" value={selectedTld} onChange={(e) => handleTldChange(e.target.value)}
                          className="h-12 px-3 rounded-xl border border-slate-200 bg-white text-sm font-medium">
                          {TLDS.map(tld => <option key={tld} value={tld}>{tld}</option>)}
                        </select>
                      </div>
                      {domainStatus === "available" && !reservation && (
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl border border-green-100">
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle2 className="w-5 h-5" />
                            <span className="text-sm font-medium">{customDomain}{selectedTld} ist verfügbar!</span>
                          </div>
                          <Button data-testid="reserve-domain-btn" size="sm" onClick={reserveDomain} disabled={reserving}
                            className="rounded-full bg-green-600 hover:bg-green-700 text-xs">
                            {reserving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lock className="w-3 h-3 mr-1" />}Jetzt sichern
                          </Button>
                        </div>
                      )}
                      {reservation && (
                        <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-100">
                          <div className="flex items-center gap-2 text-indigo-700">
                            <Lock className="w-5 h-5" />
                            <div>
                              <span className="text-sm font-medium">{reservation.domain} gesichert!</span>
                              <p className="text-xs text-indigo-500">Reservierung gültig für {reservation.ttl_minutes} Minuten</p>
                            </div>
                            <span className="ml-auto text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">Inklusive</span>
                          </div>
                        </div>
                      )}
                      {domainStatus === "taken" && (
                        <div className="p-3 bg-red-50 rounded-xl border border-red-100">
                          <div className="flex items-center gap-2 text-red-600">
                            <AlertCircle className="w-5 h-5" /><span className="text-sm">{customDomain}{selectedTld} ist bereits vergeben</span>
                          </div>
                        </div>
                      )}
                      {domainStatus === "error" && domainError && (
                        <div className="p-3 bg-amber-50 rounded-xl border border-amber-100">
                          <div className="flex items-center gap-2 text-amber-700">
                            <AlertCircle className="w-5 h-5" /><span className="text-sm">{domainError}</span>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* Option B: Connect existing domain (zweite Option) */}
                  <button
                    data-testid="checkout-connect-option"
                    onClick={() => { setDomainType("connect"); setReservation(null); }}
                    className={`w-full p-5 rounded-xl border-2 text-left transition-all ${
                      domainType === "connect" ? "border-indigo-500 bg-indigo-50/50" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${domainType === "connect" ? "bg-indigo-100 text-indigo-600" : "bg-slate-100 text-slate-400"}`}>
                        <Link2 className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 mb-1">Bestehende Domain verbinden</p>
                        <p className="text-sm text-slate-500">Ich habe bereits eine Domain – nur DNS verbinden</p>
                        <p className="text-xs text-slate-400 mt-1">Domain bleibt bei deinem Anbieter</p>
                      </div>
                      {domainType === "connect" && (
                        <div className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                    </div>
                  </button>

                  {/* Connect Domain Flow */}
                  {domainType === "connect" && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-3 pt-1">
                      {connectStep === "input" && (
                        <div className="space-y-2">
                          <div className="flex items-start gap-2 px-3 py-2 bg-green-50 border border-green-100 rounded-lg">
                            <Shield className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-green-800">Deine bestehende Webseite bleibt online, bis du umstellst.</p>
                          </div>
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <input
                                data-testid="connect-domain-input"
                                type="text"
                                value={connectDomain}
                                onChange={e => setConnectDomain(e.target.value.trim().toLowerCase())}
                                placeholder="example.de"
                                className="w-full pl-9 pr-4 h-12 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none text-sm"
                                onKeyDown={e => e.key === "Enter" && connectDomain && setupCloudflare()}
                              />
                            </div>
                            <Button
                              data-testid="connect-domain-setup-btn"
                              onClick={setupCloudflare}
                              disabled={!connectDomain.trim() || detectingCF}
                              className="h-12 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shrink-0"
                            >
                              {detectingCF ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verbinden"}
                            </Button>
                          </div>
                        </div>
                      )}

                      {connectStep === "instructions" && (
                        <div className="space-y-3">
                          {detectedProvider && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg">
                              <CheckCircle2 className="w-4 h-4 text-indigo-600 shrink-0" />
                              <p className="text-xs text-indigo-800"><strong>Erkannt:</strong> Domain liegt bei <strong>{detectedProvider}</strong></p>
                            </div>
                          )}
                          {nameservers.length > 0 ? (
                            <div className="bg-slate-900 rounded-xl p-4 font-mono text-xs" data-testid="checkout-ns-records">
                              <p className="text-slate-400 uppercase tracking-wider mb-2 text-[10px]">Nameserver bei deinem Anbieter setzen:</p>
                              {nameservers.map((ns, i) => (
                                <div key={ns} className="flex items-center justify-between bg-slate-800 rounded-lg px-3 py-2 mb-1.5">
                                  <span><span className="text-amber-400 font-bold">NS{i + 1}</span><span className="text-slate-400 mx-2">→</span><span className="text-green-400">{ns}</span></span>
                                  <button onClick={() => copyToClipboard(ns)} className="text-slate-500 hover:text-white ml-2"><Copy className="w-3.5 h-3.5" /></button>
                                </div>
                              ))}
                              <p className="text-slate-500 text-[10px] mt-2">Kann bis zu 48h dauern. Du kannst jetzt bezahlen und danach umstellen.</p>
                            </div>
                          ) : cfError ? (
                            <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-xs text-amber-800">{cfError} – Du kannst trotzdem fortfahren.</p>
                            </div>
                          ) : null}

                          {/* Anleitung für Anbieter */}
                          {nameservers.length > 0 && (
                            <div className="space-y-1.5">
                              {Object.keys(CONNECT_GUIDES).map(p => (
                                <div key={p} className="border border-slate-200 rounded-xl overflow-hidden bg-white text-sm">
                                  <button onClick={() => setOpenGuide(openGuide === p ? null : p)}
                                    className="w-full flex items-center justify-between px-4 py-2.5 font-medium text-slate-800 hover:bg-slate-50">
                                    <span>{p}</span>
                                    {openGuide === p ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                  </button>
                                  {openGuide === p && (
                                    <div className="px-4 pb-3 bg-slate-50 border-t border-slate-100">
                                      <ol className="space-y-1.5 mt-2">
                                        {CONNECT_GUIDES[p].map((s, i) => (
                                          <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                                            <span className="w-4 h-4 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                                            {s}
                                          </li>
                                        ))}
                                      </ol>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          <button onClick={() => { setConnectStep("input"); setNameservers([]); setDetectedProvider(null); }}
                            className="text-xs text-slate-400 hover:text-slate-600 underline">Domain ändern</button>
                        </div>
                      )}
                    </motion.div>
                  )}

                </div>

                {/* Next Button */}
                <div className="max-w-lg mx-auto mt-8 flex justify-end">
                  <Button
                    data-testid="checkout-next-domain"
                    onClick={goNext}
                    disabled={domainType === "custom" && !reservation || domainType === "connect" && !connectDomain.trim()}
                    className="h-12 px-8 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-base"
                  >
                    Weiter zum Paket <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* STEP 2: Package Selection */}
            {step === 1 && (
              <motion.div key="package" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-testid="package-step-title">Wähle dein Paket</h2>
                  <p className="text-slate-500">Alle Pakete monatlich kündbar – inklusive Hosting & Wartung.</p>
                </div>

                <div className="grid md:grid-cols-3 gap-4" data-testid="checkout-packages">
                  {packages.map((pkg) => {
                    const config = tierConfig[pkg.id] || tierConfig.basis;
                    const Icon = config.icon;
                    const isSelected = selectedPackage === pkg.id;
                    const isPopular = pkg.popular;
                    const isPremium = pkg.id === "premium";

                    return (
                      <button
                        key={pkg.id}
                        data-testid={`checkout-pkg-${pkg.id}`}
                        onClick={() => setSelectedPackage(pkg.id)}
                        className={`relative rounded-2xl border-2 text-left transition-all overflow-hidden ${
                          isPremium
                            ? `${isSelected ? "border-amber-500 shadow-lg shadow-amber-100/50" : "border-slate-700 hover:border-slate-600"} bg-gradient-to-b from-slate-900 to-slate-800`
                            : isSelected
                              ? "border-indigo-500 shadow-lg shadow-indigo-100/50 bg-white"
                              : "border-slate-200 hover:border-slate-300 bg-white"
                        } ${isPopular && !isPremium ? "md:-mt-2" : ""}`}
                      >
                        {/* Badge */}
                        {isPopular && (
                          <div className="bg-indigo-600 text-white text-center py-1.5 text-xs font-semibold flex items-center justify-center gap-1">
                            <Star className="w-3 h-3 fill-current" /> Beliebteste Wahl
                          </div>
                        )}
                        {isPremium && (
                          <div className="bg-amber-400/20 text-amber-300 text-center py-1.5 text-xs font-semibold">
                            Sorglos-Paket
                          </div>
                        )}

                        <div className={`p-5 ${!isPopular && !isPremium ? "pt-6" : ""}`}>
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${
                            isPremium ? "bg-amber-400/20 text-amber-400" :
                            "bg-indigo-50 text-indigo-600"
                          }`}>
                            <Icon className="w-4 h-4" />
                          </div>

                          <h3 className={`text-lg font-bold ${isPremium ? "text-white" : "text-slate-900"}`}>{pkg.name}</h3>
                          <p className={`text-xs font-medium mb-3 ${isPremium ? "text-amber-400" : "text-indigo-600"}`}>
                            {pkg.headline || config.headline}
                          </p>

                          <div className="flex items-baseline gap-0.5 mb-3">
                            <span className={`text-3xl font-bold ${isPremium ? "text-white" : "text-slate-900"}`}>{pkg.price.toFixed(0)}</span>
                            <span className={`text-sm ${isPremium ? "text-slate-400" : "text-slate-400"}`}>€/{pkg.interval}</span>
                          </div>

                          <p className={`text-xs mb-4 leading-relaxed ${isPremium ? "text-slate-400" : "text-slate-500"}`}>
                            {pkg.description}
                          </p>

                          <ul className="space-y-2">
                            {pkg.features.map((f, i) => (
                              <li key={i} className={`flex items-start gap-2 text-xs ${isPremium ? "text-slate-300" : "text-slate-600"}`}>
                                <CheckCircle2 className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isPremium ? "text-amber-400" : "text-green-500"}`} />
                                <span>{f}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Selection indicator */}
                        {isSelected && (
                          <div className={`absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center ${
                            isPremium ? "bg-amber-500" : "bg-indigo-600"
                          }`}>
                            <Check className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Upgrade hint for Basis */}
                {selectedPackage === "basis" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-lg mx-auto mt-4"
                    data-testid="upgrade-hint"
                  >
                    <div className="flex items-center gap-2 px-4 py-3 bg-indigo-50 rounded-xl border border-indigo-100">
                      <Info className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                      <p className="text-sm text-indigo-700">Tipp: Mit <strong>Pro</strong> bekommst du automatische Backups und 5 Änderungen/Monat.</p>
                    </div>
                  </motion.div>
                )}

                <div className="max-w-lg mx-auto mt-8 flex justify-between gap-3">
                  <Button variant="outline" onClick={goBack} className="h-12 px-4 sm:px-6 rounded-xl">
                    <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" /> Zurück
                  </Button>
                  <Button data-testid="checkout-next-package" onClick={goNext} className="h-12 px-4 sm:px-8 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-sm sm:text-base">
                    <span className="hidden sm:inline">Weiter zur Zusammenfassung</span><span className="sm:hidden">Weiter</span> <ArrowRight className="w-4 h-4 ml-1 sm:ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* STEP 3: Summary & Pay */}
            {step === 2 && (
              <motion.div key="summary" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-testid="summary-step-title">Zusammenfassung & Bezahlung</h2>
                  <p className="text-slate-500">Prüfe deine Auswahl und starte durch.</p>
                </div>

                {summary ? (
                  <div className="max-w-lg mx-auto space-y-4">
                    {/* Company Info */}
                    <div className="bg-white rounded-xl p-5 border border-slate-200">
                      <p className="text-sm text-slate-500">Deine Landingpage</p>
                      <p className="text-lg font-semibold text-slate-900">{summary.company_name}</p>
                      <p className="text-sm text-slate-500">{summary.industry}</p>
                    </div>

                    {/* Domain */}
                    <div className="bg-white rounded-xl p-5 border border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Globe className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="text-sm text-slate-500">Domain</p>
                            <p className="font-medium text-slate-900">{summary.domain?.name}</p>
                          </div>
                        </div>
                        <span className="text-xs font-medium text-green-600 bg-green-50 px-2.5 py-1 rounded-full">Inklusive</span>
                      </div>
                    </div>

                    {/* Package */}
                    <div className="bg-white rounded-xl p-5 border border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Zap className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="text-sm text-slate-500">Paket</p>
                            <p className="font-medium text-slate-900">{summary.package?.name}</p>
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-slate-700">{summary.package?.price}€/{summary.package?.interval}</span>
                      </div>
                      <ul className="space-y-1.5 pl-8">
                        {summary.package?.features?.map((f, i) => (
                          <li key={i} className={`text-xs flex items-center gap-1.5 ${isFeatureHighlighted(f) ? "text-slate-800 font-medium" : "text-slate-500"}`}>
                            <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" /> {f}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Simplified Total - Domain integrated into package */}
                    <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100" data-testid="checkout-total">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-indigo-700">Gesamt</span>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-indigo-700">{summary.total?.package_monthly}€</span>
                          <span className="text-indigo-500 text-sm">/Monat</span>
                        </div>
                      </div>
                      <p className="text-xs text-indigo-400 mt-2">Monatlich kündbar.</p>
                    </div>

                    {/* Enhanced Trust Badges */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="trust-badges">
                      <div className="flex items-center gap-2 px-3 py-2.5 bg-white rounded-lg border border-slate-100">
                        <Lock className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-xs text-slate-600 font-medium">Sicher, verschlüsselt & DSGVO-konform</span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-2.5 bg-white rounded-lg border border-slate-100">
                        <Shield className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <span className="text-xs text-slate-600 font-medium">Monatlich kündbar, keine Bindung</span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-2.5 bg-white rounded-lg border border-slate-100">
                        <CreditCard className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                        <span className="text-xs text-slate-600 font-medium">Sichere Zahlung via Stripe</span>
                      </div>
                    </div>

                    {/* Additional trust: mobile optimized, support */}
                    <div className="flex flex-wrap gap-3 justify-center text-xs text-slate-400">
                      <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-400" /> 100% mobiloptimiert</span>
                      <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-400" /> Support verfügbar</span>
                      <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-400" /> SSL inklusive</span>
                    </div>

                    {/* Legal Checkboxes */}
                    <div className="space-y-3" data-testid="legal-checkboxes">
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={acceptedAGB}
                          onChange={(e) => setAcceptedAGB(e.target.checked)}
                          className="mt-1 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          data-testid="accept-agb-checkbox"
                        />
                        <span className="text-sm text-slate-600 group-hover:text-slate-900 transition-colors">
                          Ich habe die{" "}
                          <a href="/agb" target="_blank" className="text-indigo-600 hover:text-indigo-800 underline font-medium">AGB</a>
                          {", den "}
                          <a href="/avv" target="_blank" className="text-indigo-600 hover:text-indigo-800 underline font-medium" data-testid="checkout-avv-link">Auftragsverarbeitungsvertrag</a>
                          {" "}und die{" "}
                          <a href="/datenschutz" target="_blank" className="text-indigo-600 hover:text-indigo-800 underline font-medium">Datenschutzerklärung</a>
                          {" "}gelesen und akzeptiere diese. *
                        </span>
                      </label>

                      {domainType === "custom" && (
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={acceptedDomainRisk}
                            onChange={(e) => setAcceptedDomainRisk(e.target.checked)}
                            className="mt-1 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            data-testid="accept-domain-risk-checkbox"
                          />
                          <span className="text-sm text-slate-600 group-hover:text-slate-900 transition-colors">
                            Ich bestätige, dass mir keine Rechte Dritter an dem gewählten Domainnamen bekannt sind und ich das Risiko einer möglichen markenrechtlichen Beanstandung selbst trage. *
                          </span>
                        </label>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-between gap-3 pt-4">
                      <Button variant="outline" onClick={goBack} className="h-12 px-4 sm:px-6 rounded-xl">
                        <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" /> Zurück
                      </Button>
                      <Button
                        data-testid="checkout-pay-btn"
                        onClick={startCheckout}
                        disabled={checkoutLoading || !acceptedAGB || (domainType === "custom" && !acceptedDomainRisk)}
                        className="h-12 px-4 sm:px-8 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-sm sm:text-base shadow-lg shadow-indigo-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {checkoutLoading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <><span className="hidden sm:inline">Jetzt Seite sichern & starten</span><span className="sm:hidden">Jetzt starten</span> <ArrowRight className="w-4 h-4 ml-1 sm:ml-2" /></>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center"><Loader2 className="w-8 h-8 text-indigo-600 animate-spin" /></div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
